import moment from "moment";
import { useMemo } from "react";

import { useFetchClassrooms } from "links/lib/features/classrooms";
import useFetchPracticeSets from "links/lib/features/practiceSets/useFetchPracticeSets";
import useFetchSessions from "links/lib/features/sessions/useFetchSessions";
import {
  IPracticeSet,
  IPracticeSetSession,
  SessionType,
} from "links/lib/types";

import { useFetchPracticeSetsByUserExperience } from "../features/practiceSets";

const LIVE_SESSION_REFETCH_INTERVAL = 10000;

export type LiveBanner = {
  classroomName: string;
  session: IPracticeSetSession;
  practiceSetName: string;
  classroomId: string;
};

export const useGetClassroomLiveBanners = ({
  classroomId = "",
  showClassroomName,
  isStudent = false,
}: {
  classroomId?: string;
  showClassroomName?: boolean;
  isStudent?: boolean;
}): LiveBanner[] => {
  const activeSessionStartTime = useMemo(
    () => moment().startOf("minute").subtract(24, "h").toDate(),
    []
  );

  const fetchSessions = useFetchSessions({
    incomplete_sessions_only: true,
    start_time: activeSessionStartTime,
    group_id: classroomId,
    keepPreviousData: true,
    refetchInterval: LIVE_SESSION_REFETCH_INTERVAL,
  });

  const classroomsFetch = useFetchClassrooms({
    archived: false,
    disabled: !showClassroomName,
    id: classroomId,
    includeAllRoles: true,
  });

  const practiceSetIds = useMemo(() => {
    return fetchSessions.data?.sessions?.map((s) => s.practice_set_id) || [];
  }, [fetchSessions.data?.sessions]);

  const practiceSetDetailsFetch = useFetchPracticeSets({
    ids: practiceSetIds || undefined,
    enabled: !isStudent,
    include_all: true,
  });

  const studentPracticeSetDetailsFetch = useFetchPracticeSetsByUserExperience({
    enabled: isStudent,
  });

  //todo make a util bc this is copy pasted
  const practiceSetDetailMap: {
    [key: string]: {
      practice_set: IPracticeSet;
      maximum_xp: number;
      item_count: number;
      classroom_id: string;
    };
  } = useMemo(() => {
    const map: {
      [key: string]: {
        practice_set: IPracticeSet;
        maximum_xp: number;
        item_count: number;
        classroom_id: string;
      };
    } = {};

    studentPracticeSetDetailsFetch.data?.classroom_practice_sets
      .flatMap((c) =>
        c.practice_sets_with_user_details.map((p) => ({
          ...p,
          classroom_id: c.classroom_id,
        }))
      )
      .forEach((p) => {
        map[p.practice_set.id] = p;
      });

    return map;
  }, [studentPracticeSetDetailsFetch.data?.classroom_practice_sets]);

  const liveBanners = useMemo(() => {
    if (!fetchSessions.data) return [];

    const getPracticeSetTitle = (id: string) => {
      if (isStudent) return practiceSetDetailMap[id]?.practice_set.title ?? "";

      const practiceSets = practiceSetDetailsFetch.data?.practice_sets || [];
      return practiceSets.find((set) => set.id === id)?.title ?? "";
    };

    const activeSessions = fetchSessions.data.sessions;

    return activeSessions
      .filter((s: IPracticeSetSession) => {
        return s.session_type === SessionType.Class;
      })
      .flatMap((s: IPracticeSetSession) => {
        const classroom = classroomsFetch.data?.classrooms?.find(
          (c) => c.id === s.group_id
        );
        if (showClassroomName && !classroom) return [];

        return [
          {
            classroomId: s.group_id,
            classroomName: classroom?.name || "",
            session: s,
            practiceSetName: getPracticeSetTitle(s.practice_set_id),
          },
        ];
      });
  }, [
    showClassroomName,
    fetchSessions,
    classroomsFetch.data?.classrooms,
    isStudent,
    practiceSetDetailMap,
    practiceSetDetailsFetch.data?.practice_sets,
  ]);

  return liveBanners;
};
